<script lang="ts">
  import { cx } from '@emotion/css';
  import type { Option } from './types';
  import CheckboxChecked from '$components/Icons/CheckboxChecked.svelte';
  import CheckboxUnchecked from '$components/Icons/CheckboxUnchecked.svelte';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';

  interface Props {
    option: Option;
    isSelected?: boolean;
    handleOption: (event: Event) => void;
    handleOptionKeyDown: (event: KeyboardEvent) => void;
  }

  let { option, isSelected = false, handleOption, handleOptionKeyDown }: Props = $props();

  const responsiveVars = new ResponsiveVars()
    .addFluid('option-height', toFluidVarInput(48, 48))
    .addFluid('option-padding-x', toFluidVarInput(15, 15))
    .addFluid('option-font-size', toFluidVarInput(14, 14))
    .addFluid('option-padding-y', toFluidVarInput(15, 15))
    .addFluid('option-padding-right', toFluidVarInput(3, 3))
    .addFluid('option-checkbox-size', toFluidVarInput(15, 15))
    .toCSS();
</script>

<li
  class={cx(responsiveVars, 'option')}
  role="option"
  onclick={handleOption}
  onkeydown={handleOptionKeyDown}
  tabindex="0"
  data-value={option.value}
  aria-selected={isSelected}
  aria-label={`${option.label} ${isSelected ? 'selected' : 'not selected'}`}
>
  <span class="option-inner">
    <span class="option__label">
      {option.label}
    </span>
    <span class="option__checkbox">
      {#if isSelected}
        <CheckboxChecked />
      {:else}
        <CheckboxUnchecked />
      {/if}
    </span>
  </span>
</li>

<style lang="scss">
  .option {
    list-style-type: none;
    width: 100%;
    min-height: var(--option-height);
    margin-top: -1px;
    border: none;
    background: none;
    padding: 0 var(--option-padding-x);
    font-family: var(--font-family-primary);
    font-weight: 500;
    font-size: var(--option-font-size);
    line-height: 130%; // var(--twist-core-line-height-130);
    cursor: pointer;

    &:last-of-type .option-inner {
      border-bottom: none;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .option:hover,
    .option:active {
      background-color: var(--twist-color-gray-0);
      filter: invert(100%);
      outline: none;

      .option-inner {
        border-bottom: none;
      }
    }
  }

  .option:focus-visible {
    outline: none;
    border: 1px solid var(--twist-color-gray-100);

    .option-inner {
      border-bottom: none;
    }
  }

  .option-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--twist-color-gray-100);
    padding: var(--option-padding-y) var(--option-padding-right) var(--option-padding-y) 0;
  }

  .option__label {
    max-width: 90%;
  }

  .option__checkbox {
    height: var(--option-checkbox-size);
    width: var(--option-checkbox-size);
  }
</style>
